/**
 * The Check-ins tab for an HRT / TRT patient: the alerts the Monthly check-in raised (developer spec tab "Check-in
 * Alerts": RED same-day review, ORANGE before the next dose or refill, YELLOW at the next touchpoint), each resolved
 * with what was done; then the check-ins themselves, the HRT answers on each, and what the next Monthly link asks.
 *
 * Resolving is a clinical act: clinicians and admins only (the API refuses STAFF). The reason is stored on the
 * alert and as a DecisionLog row, and the matching Physician Alert clears on the next sync.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { InfoHint } from './ui';
import { EmptyCard, Fold, SaveNote, StepCard, TickBox, fieldClass } from './HrtUi';
import {
  HRT_PROGRAM_KEY,
  LEVEL_TEXT,
  SWITCH_QUESTION,
  errorText,
  hrtKey,
  shortDay,
  type HrtAlert,
  type HrtPatientView,
} from '../lib/hrt';

export function HrtCheckinsTab({ view }: { view: HrtPatientView }) {
  const open = view.alerts.open;
  const resolved = view.alerts.resolved;
  return (
    <div className="space-y-2.5" data-testid="hrt-checkins-tab">
      <StepCard
        title="Check-in alerts"
        subtitle={
          open.length > 0
            ? `${open.length} open. Red is a same-day review, orange before the next dose or refill, yellow at the next touchpoint.`
            : 'Nothing open. Alerts from the Monthly check-in appear here and in Physician Alerts.'
        }
        testId="hrt-alerts"
      >
        {open.length === 0 ? (
          <p className="rounded-lg border border-dashed border-hairline px-4 py-4 text-center text-[13.5px] text-ink-muted">No open check-in alerts.</p>
        ) : (
          <ul className="space-y-2">
            {open.map((alert) => (
              <AlertCard key={alert.id} view={view} alert={alert} />
            ))}
          </ul>
        )}
        {resolved.length > 0 && (
          <Fold title="Resolved" count={resolved.length} testId="fold-resolved">
            <ul className="divide-y divide-hairline rounded-lg border border-hairline">
              {resolved.map((alert) => (
                <li key={alert.id} className="px-3 py-2.5 text-[13px]" data-testid={`resolved-${alert.id}`}>
                  <p className="flex flex-wrap items-center gap-2">
                    <LevelBadge level={alert.level} />
                    <span className="text-navy">{alert.text}</span>
                  </p>
                  <p className="mt-1 text-[12.5px] text-ink-muted">
                    Resolved {shortDay(alert.resolvedAt)}
                    {alert.resolvedBy ? ` by ${alert.resolvedBy.name}` : ''}: {alert.resolution}
                  </p>
                </li>
              ))}
            </ul>
          </Fold>
        )}
      </StepCard>

      <CheckinsCard view={view} />
    </div>
  );
}

function LevelBadge({ level }: { level: HrtAlert['level'] }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-[5px] px-1.5 py-[3px] text-[11px] font-bold uppercase leading-none tracking-[0.04em] ${LEVEL_TEXT[level].badge}`}>
      {LEVEL_TEXT[level].word}
    </span>
  );
}

function AlertCard({ view, alert }: { view: HrtPatientView; alert: HrtAlert }) {
  const clinician = useAuth().can('ADMIN', 'CLINICIAN');
  const queryClient = useQueryClient();
  const [resolving, setResolving] = useState(false);
  const [action, setAction] = useState<'acted_on' | 'dismissed'>('acted_on');
  const [resolution, setResolution] = useState('');
  // A clot, bleeding or voice change alert can record the matching evaluation in the same step, when ticked.
  const [confirm, setConfirm] = useState(false);
  const level = LEVEL_TEXT[alert.level];
  const resolve = useMutation({
    mutationFn: () =>
      api.post(`/hrt/patients/${view.patient.id}/alerts/${alert.id}/resolve`, {
        action,
        resolution: resolution.trim(),
        ...(alert.confirms && action === 'acted_on' && confirm ? { confirm: true } : {}),
      }),
    onSuccess: () => {
      for (const key of [hrtKey(view.patient.id), HRT_PROGRAM_KEY, ['notifications'], ['notification-counts'], ['nav-counts']]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  return (
    <li className={`rounded-[10px] border px-3 py-2.5 ${level.line} ${level.soft}`} data-testid={`alert-${alert.ruleId}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <LevelBadge level={alert.level} />
            <span className="text-[12px] font-semibold text-navy">{level.when}</span>
          </p>
          <p className="mt-1 text-[14px] font-semibold leading-snug text-navy">{alert.text}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] text-ink-muted">
            {alert.checkinMonth !== null ? `Month ${alert.checkinMonth} check-in · ` : ''}raised {shortDay(alert.createdAt)}
            <InfoHint label="Which rule raised it">Rule {alert.ruleId} (developer spec, Check-in Alerts)</InfoHint>
          </p>
        </div>
        {clinician && !resolving && (
          <button
            type="button"
            onClick={() => setResolving(true)}
            className="inline-flex h-[32px] shrink-0 items-center rounded-md border border-hairline bg-white px-3.5 text-[13px] font-semibold text-forest hover:bg-mint"
            data-testid={`resolve-${alert.ruleId}`}
          >
            Resolve
          </button>
        )}
      </div>
      {!clinician && <p className="mt-1.5 text-[12px] text-ink-muted">A clinician resolves this alert.</p>}
      {resolving && (
        <form
          className="mt-2.5 rounded-lg bg-white px-3 py-2.5 ring-1 ring-hairline"
          onSubmit={(event) => {
            event.preventDefault();
            if (resolution.trim().length >= 5) resolve.mutate();
          }}
          data-testid="resolve-form"
        >
          <div role="radiogroup" aria-label="What happened" className="flex flex-wrap gap-2">
            {(
              [
                ['acted_on', 'Acted on'],
                ['dismissed', 'Dismissed'],
              ] as const
            ).map(([key, word]) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={action === key}
                onClick={() => setAction(key)}
                className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium ${action === key ? 'border-forest bg-mint text-forest' : 'border-hairline bg-white text-navy hover:bg-paper'}`}
              >
                {word}
              </button>
            ))}
          </div>
          <label className="mt-2 block text-[12px] font-semibold text-navy">
            {action === 'acted_on' ? 'What was done' : 'Why it needs no action'}
            <textarea
              value={resolution}
              onChange={(event) => setResolution(event.target.value)}
              rows={2}
              maxLength={2000}
              placeholder={action === 'acted_on' ? 'For example: called the patient, ordered labs, held the next dose' : 'For example: already reviewed at the visit on Oct 20'}
              className={`${fieldClass} mt-1`}
              data-testid="resolution"
            />
          </label>
          {alert.confirms && action === 'acted_on' && (
            <button
              type="button"
              role="checkbox"
              aria-checked={confirm}
              onClick={() => setConfirm((was) => !was)}
              className="mt-2 flex items-start gap-2 text-left text-[12.5px] text-navy"
              data-testid="resolve-confirm"
            >
              <TickBox checked={confirm} size={20} />
              <span>
                Also record <b className="font-semibold">{alert.confirms.label.toLowerCase()}</b>. This lifts the pathway lock for this episode; a new
                report locks it again.
              </span>
            </button>
          )}
          <div className="mt-2 flex items-center gap-3">
            <button
              type="submit"
              disabled={resolution.trim().length < 5 || resolve.isPending}
              className="inline-flex h-[34px] items-center rounded-md bg-forest px-4 text-[13.5px] font-semibold text-white hover:bg-forest-deep disabled:opacity-50"
              data-testid="resolve-save"
            >
              {resolve.isPending ? 'Saving' : 'Resolve alert'}
            </button>
            <button type="button" onClick={() => setResolving(false)} className="text-[12.5px] text-ink-muted underline underline-offset-2">
              Cancel
            </button>
          </div>
          {resolve.isError && <SaveNote tone="error">{errorText(resolve.error)}</SaveNote>}
        </form>
      )}
    </li>
  );
}

/** Safety screens asked every month (developer spec tab "Monthly Switches", row "(always)"). */
function alwaysAsked(track: string | null): string[] {
  const all = ['Clot symptoms', 'Taking the medicine'];
  return track === 'male_trt' ? [...all, 'Ankle or leg swelling'] : all;
}

function CheckinsCard({ view }: { view: HrtPatientView }) {
  const { checkins } = view;
  const asked = checkins.nextSwitches
    ? Object.entries(checkins.nextSwitches)
        .filter(([key, value]) => key.startsWith('hrt_ask_') && value === 'true')
        .map(([key]) => SWITCH_QUESTION[key] ?? key)
    : [];
  const responses = checkins.responses ?? [];
  const byMonth = new Map<number, HrtAlert[]>();
  for (const alert of [...view.alerts.open, ...view.alerts.resolved]) {
    if (alert.checkinMonth === null) continue;
    byMonth.set(alert.checkinMonth, [...(byMonth.get(alert.checkinMonth) ?? []), alert]);
  }

  if (checkins.count === 0 && !checkins.nextSwitches) {
    return (
      <EmptyCard
        title="No Monthly check-in yet"
        body={`The HRT questions join ${view.patient.firstName}'s Monthly check-in once hormones start. Safety questions are asked every month; symptom questions every other month.`}
      />
    );
  }

  return (
    <StepCard
      title="Monthly check-ins"
      subtitle={
        checkins.last
          ? `${checkins.count} received. The last was month ${checkins.last.checkinMonth ?? '?'}, on ${shortDay(checkins.last.submittedAt)}.`
          : `${checkins.count} received.`
      }
      testId="hrt-checkins"
    >
      <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="rounded-lg bg-paper-deep px-3 py-2">
          <dt className="text-[12px] text-ink-muted">Check-ins received</dt>
          <dd className="font-display text-[20px] text-navy" style={{ fontWeight: 600 }}>{checkins.count}</dd>
        </div>
        <div className="rounded-lg bg-paper-deep px-3 py-2">
          <dt className="text-[12px] text-ink-muted">Months on hormones</dt>
          <dd className="font-display text-[20px] text-navy" style={{ fontWeight: 600 }}>{checkins.monthsOnHormones ?? 'Not started'}</dd>
        </div>
        <div className="rounded-lg bg-paper-deep px-3 py-2">
          <dt className="text-[12px] text-ink-muted">Last check-in</dt>
          <dd className="font-display text-[20px] text-navy" style={{ fontWeight: 600 }}>{checkins.last ? shortDay(checkins.last.submittedAt) : 'None yet'}</dd>
        </div>
      </dl>

      {checkins.nextSwitches && (
        <div className="mt-3 rounded-lg border border-hairline px-3 py-2.5" data-testid="next-questions">
          <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-navy">
            {checkins.nextAt ? `The next Monthly check-in (${shortDay(checkins.nextAt)}) asks` : 'If a Monthly check-in went out today, it would ask'}
            <InfoHint label="When the HRT questions are asked">
              Safety questions every month. Symptom questions every other month on hormones (months 1, 3, 5 and on), the month after a dose change
              and the month after a worrying answer (developer spec, Monthly Switches).
            </InfoHint>
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {[...alwaysAsked(view.membership.track), ...asked].map((question) => (
              <li key={question} className="rounded-[5px] bg-paper-deep px-2 py-[4px] text-[12px] text-navy">
                {question}
              </li>
            ))}
          </ul>
        </div>
      )}

      {responses.length > 0 ? (
        <ul className="mt-3 space-y-2" data-testid="checkin-responses">
          {responses.map((response) => (
            <li key={response.formResponseId} className="rounded-lg border border-hairline">
              <p className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-3 py-2 text-[13px]">
                <span className="font-semibold text-navy">{response.checkinMonth !== null ? `Month ${response.checkinMonth}` : 'Check-in'}</span>
                <span className="text-ink-muted">{shortDay(response.submittedAt)}</span>
              </p>
              {response.answers.length === 0 ? (
                <p className="px-3 py-2 text-[12.5px] text-ink-muted">No HRT questions on this check-in.</p>
              ) : (
                <dl className="divide-y divide-hairline">
                  {response.answers.map((answer) => (
                    <div key={answer.ref} className="flex items-start justify-between gap-3 px-3 py-1.5 text-[12.5px]">
                      <dt className="text-ink-muted" title={answer.question}>{answer.label}</dt>
                      <dd className="text-right font-medium text-navy">{answer.answer}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {(byMonth.get(response.checkinMonth ?? -1) ?? []).length > 0 && (
                <p className="flex flex-wrap gap-1.5 border-t border-hairline px-3 py-2">
                  {(byMonth.get(response.checkinMonth ?? -1) ?? []).map((alert) => (
                    <span key={alert.id} className="inline-flex items-center gap-1.5 text-[12px] text-navy">
                      <LevelBadge level={alert.level} />
                      {alert.text}
                    </span>
                  ))}
                </p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        byMonth.size > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="checkin-months">
            {[...byMonth.entries()]
              .sort((a, b) => b[0] - a[0])
              .map(([month, alerts]) => (
                <li key={month} className="rounded-lg border border-hairline px-3 py-2 text-[12.5px]">
                  <p className="font-semibold text-navy">Month {month} check-in</p>
                  <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {alerts.map((alert) => (
                      <span key={alert.id} className="inline-flex items-center gap-1.5 text-navy">
                        <LevelBadge level={alert.level} />
                        {alert.text}
                        {alert.resolvedAt && <span className="text-ink-muted">(resolved)</span>}
                      </span>
                    ))}
                  </p>
                </li>
              ))}
          </ul>
        )
      )}
    </StepCard>
  );
}
